// Copied from platform-app components/pos/receipt/BlockCard.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ReceiptBlock } from '@platform/shared-types';
import { BLOCK_LABELS } from './blockDefaults';
import BlockSettings from './BlockSettings';

interface Props {
  block: ReceiptBlock;
  onChange: (next: ReceiptBlock) => void;
  onDelete: () => void;
}

const alignName = (a: 'L'|'C'|'R') => ({ L: 'Left', C: 'Center', R: 'Right' }[a]);

const summary = (b: ReceiptBlock): string => {
  switch (b.type) {
    case 'text': return `${alignName(b.align)} · ${b.size} · ${[b.bold && 'Bold', b.italic && 'Italic', b.underline && 'U', b.inverse && 'Inverse'].filter(Boolean).join('+') || '—'}`;
    case 'boxed': return `${alignName(b.align)} · ${b.size} · ${b.borderStyle}`;
    case 'logo': return `${alignName(b.align)} · ${b.maxWidth}`;
    case 'image': return b.imageUrl ? `${alignName(b.align)} · ${b.maxWidth}` : 'no URL set';
    case 'qr': return b.data ? `${alignName(b.align)} · ${b.size}` : 'no data set';
    case 'divider': return b.style;
    case 'spacer': return b.height;
  }
};

const preview = (b: ReceiptBlock): string => {
  if (b.type === 'text' || b.type === 'boxed') return `"${b.content.slice(0, 40)}${b.content.length > 40 ? '…' : ''}"`;
  if (b.type === 'qr') return b.data ? `→ ${b.data.slice(0, 30)}` : '— set data —';
  return BLOCK_LABELS[b.type].label;
};

export default function BlockCard({ block, onChange, onDelete }: Props) {
  const [expanded, setExpanded] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white border border-stone-200 rounded-lg overflow-hidden text-stone-800">
      <div className="flex items-center gap-2 px-3 py-2">
        <button {...attributes} {...listeners} type="button" className="text-stone-400 cursor-grab" aria-label="drag handle">≡</button>
        <span className="text-lg">{BLOCK_LABELS[block.type].icon}</span>
        <span className="text-sm font-medium flex-1 truncate text-stone-800">{BLOCK_LABELS[block.type].label} — {preview(block)}</span>
        <span className="text-[11px] text-stone-400 hidden md:inline">{summary(block)}</span>
        <button type="button" onClick={() => setExpanded((v) => !v)} className="text-stone-500 hover:text-stone-700 text-xs" aria-label="toggle settings">
          {expanded ? '▴' : '⚙️'}
        </button>
        <button type="button" onClick={onDelete} className="text-rose-400 hover:text-rose-600 text-xs" aria-label="delete">✕</button>
      </div>
      {expanded && <BlockSettings block={block} onChange={onChange} />}
    </div>
  );
}
